import type { Schema } from '@dealdesk/api-schema';
import { z } from 'zod';

import { M, zOptionalMoney } from '@/business';

export type Kind = Schema<'BankUpdateKind'>;

/** Which inputs each bank update kind shows. Mirrors BankUpdateCreate on the server. */
export const KIND_FIELDS: Record<Kind, Partial<Record<'summary' | 'offer' | 'eligibleAmount' | 'shortfallAmount' | 'verification' | 'rejection' | 'closeDeal' | 'requiredAction' | 'documents' | 'due' | 'pendingWith', true>>> = {
  query: { summary: true, requiredAction: true, documents: true, due: true, pendingWith: true },
  document_request: { summary: true, documents: true, due: true, pendingWith: true },
  verification: { verification: true },
  eligibility_issue: { summary: true, eligibleAmount: true, requiredAction: true, due: true, pendingWith: true },
  deviation: { summary: true, requiredAction: true, due: true, pendingWith: true },
  offer: { summary: true, offer: true, pendingWith: true },
  approval: { offer: true },
  rejection: { rejection: true },
  duplicate_application: { summary: true, closeDeal: true },
  information: { summary: true, shortfallAmount: true },
};

export const composerSchema = z
  .object({
    kind: z.enum(['query', 'document_request', 'verification', 'eligibility_issue', 'deviation', 'offer', 'approval', 'rejection', 'duplicate_application', 'information']),
    categoryCode: z.string().optional(),
    bankText: z.string({ error: M.required }).trim().min(1, M.required),
    summary: z.string().optional(),
    requiredAction: z.string().optional(),
    sourcePartyId: z.string().optional(),
    source: z.string().optional(),
    dueOn: z.string().optional(),
    pendingWith: z.enum(['none', 'customer', 'internal_user', 'bank']),
    pendingUserId: z.string().optional(),
    eligibleAmount: zOptionalMoney({ min: '1.00' }),
    shortfallAmount: zOptionalMoney({ min: '1.00' }),
    verificationOutcome: z.enum(['answered', 'no_answer', 'unreachable', 'customer_abroad']),
    offerAmount: zOptionalMoney({ min: '1.00' }),
    tenorMonths: z.string().regex(/^\d{0,3}$/, M.tenor).optional(),
    profitRate: z.string().regex(/^(\d{1,2}(\.\d{1,4})?)?$/, 'validation.rate').optional(),
    instalment: zOptionalMoney({ min: '1.00' }),
    firstInstalmentOn: z.string().optional(),
    gracePeriodDays: z.string().regex(/^\d{0,3}$/, 'validation.wholeNumber').optional(),
    rejectionReasonCode: z.string().optional(),
    closeDeal: z.boolean(),
    requestedDocumentTypes: z.array(z.string()),
  })
  .superRefine((v, ctx) => {
    const f = KIND_FIELDS[v.kind];
    if (f.offer && !v.offerAmount) ctx.addIssue({ code: 'custom', path: ['offerAmount'], message: M.required });
    if (f.rejection && !v.rejectionReasonCode) ctx.addIssue({ code: 'custom', path: ['rejectionReasonCode'], message: M.required });
    if (f.pendingWith && v.pendingWith === 'internal_user' && !v.pendingUserId) ctx.addIssue({ code: 'custom', path: ['pendingUserId'], message: M.required });
  });
export type ComposerValues = z.infer<typeof composerSchema>;

/** Form values → API payload. Only the fields that apply to the kind are sent. */
export function toBankUpdateCreate(v: ComposerValues, bankId: string): Schema<'BankUpdateCreate'> {
  const f = KIND_FIELDS[v.kind];
  const text = (s?: string) => (s?.trim() ? s.trim() : undefined);
  const int = (s?: string) => (s?.trim() ? Math.trunc(Number(s)) : undefined);
  const offerKind: Schema<'BankOfferCreate'>['kind'] = v.kind === 'approval' ? 'approval' : 'counter_offer';
  const body: Schema<'BankUpdateCreate'> = { kind: v.kind, bankText: v.bankText.trim() };
  const set = <K extends keyof Schema<'BankUpdateCreate'>>(k: K, val: Schema<'BankUpdateCreate'>[K] | undefined) => {
    if (val !== undefined) body[k] = val;
  };
  set('categoryCode', text(v.categoryCode));
  set('source', text(v.source));
  set('sourcePartyId', text(v.sourcePartyId));
  if (f.summary) set('summary', text(v.summary));
  if (f.requiredAction) set('requiredAction', text(v.requiredAction));
  if (f.due) set('dueOn', text(v.dueOn));
  if (f.eligibleAmount) set('eligibleAmount', v.eligibleAmount ?? undefined);
  if (f.eligibleAmount || f.shortfallAmount) set('shortfallAmount', v.shortfallAmount ?? undefined);
  if (f.verification) set('verificationOutcome', v.verificationOutcome);
  if (f.rejection) set('rejectionReasonCode', text(v.rejectionReasonCode));
  if (f.closeDeal) set('closeDeal', v.closeDeal);
  if (f.documents && v.requestedDocumentTypes.length) set('requestedDocumentTypes', v.requestedDocumentTypes);
  if (f.offer && v.offerAmount) {
    const tenor = int(v.tenorMonths);
    const grace = int(v.gracePeriodDays);
    const rate = text(v.profitRate);
    const first = text(v.firstInstalmentOn);
    const reason = text(v.summary);
    set('offer', {
      kind: offerKind,
      amount: v.offerAmount,
      ...(tenor ? { tenorMonths: tenor } : {}),
      ...(rate ? { profitRate: rate } : {}),
      ...(v.instalment ? { instalment: v.instalment } : {}),
      ...(first ? { firstInstalmentOn: first } : {}),
      ...(grace !== undefined ? { gracePeriodDays: grace } : {}),
      ...(reason ? { reason } : {}),
    });
  }
  if (f.pendingWith && v.pendingWith !== 'none') {
    const reason = text(v.summary) ?? text(v.bankText.slice(0, 120)) ?? '';
    set('pendingWith', v.pendingWith === 'internal_user' ? { type: 'internal_user', userId: v.pendingUserId ?? '', reason } : v.pendingWith === 'bank' ? { type: 'bank', bankId, reason } : { type: 'customer', reason });
  }
  return body;
}
